import { DateTime } from "luxon";
import type { TournamentInput, TournamentRecord } from "./tournaments-api";

export interface TournamentForm {
  name: string;
  game: string;
  description: string;
  rules: string;
  registration_instructions: string;
  contact_phone: string;
  starts_at: string;
  ends_at: string;
}
export const emptyTournamentForm: TournamentForm = {
  name: "", game: "", description: "", rules: "", registration_instructions: "", contact_phone: "", starts_at: "", ends_at: "",
};

export function tournamentToForm(event: TournamentRecord): TournamentForm {
  return {
    name: event.name, game: event.game, description: event.description ?? "", rules: event.rules ?? "",
    registration_instructions: event.registration_instructions ?? "", contact_phone: event.contact_phone ?? "",
    starts_at: DateTime.fromISO(event.starts_at).setZone(event.venue_timezone).toFormat("yyyy-MM-dd'T'HH:mm"),
    ends_at: DateTime.fromISO(event.ends_at).setZone(event.venue_timezone).toFormat("yyyy-MM-dd'T'HH:mm"),
  };
}

export type TournamentFormError = "name" | "game" | "dates" | "order" | "future" | "entry" | "phone";

/** Patch only the fields the operator changed. A resolved fallback phone must not become a stored override during an unrelated edit. */
export function tournamentFormPatch(form: TournamentForm, originalForm: TournamentForm, record: TournamentRecord, input: TournamentInput): Partial<TournamentInput> & { expected_updated_at: string } {
  const changed = Object.fromEntries(Object.entries(input).filter(([key]) => key === "status"
    ? input.status !== record.status
    : form[key as keyof TournamentForm] !== originalForm[key as keyof TournamentForm]));
  return { ...changed, expected_updated_at: record.updated_at };
}

export function tournamentFormInput(form: TournamentForm, timezone: string, status: TournamentInput["status"], now = Date.now(), original?: TournamentRecord | null): { input: TournamentInput; error?: never } | { error: TournamentFormError; input?: never } {
  if (form.name.trim().length < 3 || form.name.trim().length > 120) return { error: "name" };
  if (form.game.trim().length < 2 || form.game.trim().length > 100) return { error: "game" };
  const start = DateTime.fromISO(form.starts_at, { zone: timezone });
  const end = DateTime.fromISO(form.ends_at, { zone: timezone });
  // Reject invalid or DST-skipped local times rather than silently shifting the event.
  if (!start.isValid || !end.isValid || start.toFormat("yyyy-MM-dd'T'HH:mm") !== form.starts_at || end.toFormat("yyyy-MM-dd'T'HH:mm") !== form.ends_at) return { error: "dates" };
  if (end.toMillis() <= start.toMillis()) return { error: "order" };
  const originalForm = original ? tournamentToForm(original) : null;
  const unchangedSchedule = originalForm?.starts_at === form.starts_at && originalForm?.ends_at === form.ends_at;
  if (status === "published" && start.toMillis() <= now && !(original?.status === "published" && unchangedSchedule)) return { error: "future" };
  if (status === "published" && !form.registration_instructions.trim()) return { error: "entry" };
  if (form.contact_phone.trim() && (!/^\+?[\d\s()-]+$/.test(form.contact_phone.trim()) || !/^\+?\d{7,15}$/.test(form.contact_phone.trim().replace(/[\s()-]/g, "")))) return { error: "phone" };
  return { input: {
    name: form.name.trim(), game: form.game.trim(), description: form.description.trim() || null, rules: form.rules.trim() || null,
    registration_instructions: form.registration_instructions.trim() || null, contact_phone: form.contact_phone.trim() || null,
    starts_at: unchangedSchedule && original ? original.starts_at : start.toUTC().toISO()!, ends_at: unchangedSchedule && original ? original.ends_at : end.toUTC().toISO()!, status,
  } };
}
